import React from 'react';
import { makeStyles } from '@material-ui/core';
import { Button, Color, Flex, FontAwesome, Text } from 'component-library';
import { useCommonStores } from 'stores/useStores';
import { observer } from 'mobx-react';

export const SCHEDULE_A_CALL_URL =
  'https://calendly.com/d/cx7z-ddd-36y/mainstreet-tax-credits';

const openScheduleACall = () =>
  window.open(SCHEDULE_A_CALL_URL, '_blank', 'noopener,noreferrer');

const useStyles = makeStyles(() => ({
  scheduleBtn: {
    backgroundColor: Color.blue._20,
    borderRadius: 24,
    transition: 'background-color .2s',
    padding: '12px 16px 12px 12px',

    '&:hover': {
      backgroundColor: Color.blue._10,
      '& $scheduleBtnIcon': {
        backgroundColor: Color.blue._60,
      },
    },
  },
  scheduleBtnIcon: {
    backgroundColor: Color.blue._50,
    borderRadius: '50%',
    width: 32,
    height: 32,
    marginRight: 8,
    transition: 'background-color .2s',
  },
  icon: {
    lineHeight: '24px',
  },
  floatTopRight: {
    position: 'absolute',
    top: 16,
    right: 24,
    zIndex: 10,
  },
}));

interface ScheduleACallButtonProps {
  /**
   * Visual style:
   * - 'pill' (default): filled light-blue pill with an icon badge. Suits the
   *   light-background funnel screens (account creation, eligible tax credits).
   * - 'inline': borderless tertiary button with a calendar icon and light
   *   text. Matches the secondary buttons on the Discover ("/welcome") hero,
   *   which sits on a dark background.
   */
  variant?: 'pill' | 'inline';
  /**
   * Pins the button to the top-right corner of its nearest positioned
   * ancestor. Only meaningful for the 'pill' variant.
   */
  floatTopRight?: boolean;
  className?: string;
}

/**
 * Button that opens the MainStreet "Schedule a Call" Google Calendar booking
 * page in a new tab. Unlike TalkToAnExpertButton, this links out directly
 * rather than opening an in-app modal.
 */
const ScheduleACallButton: React.FC<ScheduleACallButtonProps> = observer(
  ({ variant = 'pill', floatTopRight, className }) => {
    const classes = useStyles();
    const { app } = useCommonStores();

    if (variant === 'inline') {
      return (
        <Button
          className={className}
          variant='tertiary'
          dataTestId='schedule-a-call-button'
          label={
            <Flex gap={8} alignItems='center'>
              <FontAwesome
                name='calendar'
                variant='solid'
                size={16}
                color={Color.neutral._20}
              />
              <Text color={Color.neutral._20}>Schedule a Call</Text>
            </Flex>
          }
          onClick={openScheduleACall}
        />
      );
    }

    return (
      <Button
        className={`${classes.scheduleBtn} ${
          floatTopRight ? classes.floatTopRight : ''
        } ${className ?? ''}`}
        variant='tertiary'
        dataTestId='schedule-a-call-button'
        label={
          <Flex alignItems='center' justifyContent='center'>
            {!app.isMobile && (
              <Flex
                basis={40}
                justifyContent='center'
                alignItems='center'
                className={classes.scheduleBtnIcon}
              >
                <FontAwesome
                  variant='solid'
                  name='calendar'
                  size={16}
                  color={Color.neutral.white}
                  className={classes.icon}
                />
              </Flex>
            )}
            <Text variant='medium' color={Color.blue._60}>
              Schedule a Call
            </Text>
          </Flex>
        }
        onClick={openScheduleACall}
      />
    );
  },
);

export default ScheduleACallButton;
